import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { MaskedLines, Reveal } from "../components/Reveal";
import { Marquee } from "../components/Marquee";
import { MonkeyMark } from "../components/Logo";

const chapters = [
  {
    num: "01",
    title: "Curiosity First",
    color: "#FFC72C",
    text: "Kids are born scientists. They ask why the sky is blue, where dinosaurs went, and how rockets fly. KITOE entertainment exists to protect that curiosity — every quiz, puzzle and quest we design starts with a question a child genuinely wants to answer. When wonder leads, learning follows naturally.",
  },
  {
    num: "02",
    title: "Safe by Design",
    color: "#34D399",
    text: "Parents deserve peace of mind. Our games have no ads, no manipulative in-app purchases and no endless noise. Just calm, colorful, carefully crafted play. We build every app the way we'd want it for our own kids — gentle, honest and respectful of young minds.",
  },
  {
    num: "03",
    title: "Play as Pedagogy",
    color: "#38BDF8",
    text: "The best learning doesn't feel like learning. Earning a star for knowing a planet, unlocking a badge for solving a riddle — these tiny celebrations build real confidence. We turn practice into adventure, so kids come back not because they have to, but because they can't wait to.",
  },
];

export default function About() {
  return (
    <main>
      <section className="px-4 sm:px-8 pt-14 sm:pt-20 pb-16">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7">
            <span className="text-xs font-extrabold uppercase tracking-[0.2em] text-coral">
              Our story
            </span>
            <h1 className="mt-2 font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight">
              <MaskedLines lines={["Made by someone", "who never stopped", "asking why"]} />
            </h1>
            <Reveal delay={0.35}>
              <div className="mt-7 space-y-5 text-base sm:text-lg text-ink/75 leading-relaxed max-w-2xl">
                <p>
                  Hi, I'm Keith — the maker behind KITOE entertainment. I started
                  building educational games because I watched how kids light up when
                  they figure something out on their own. That spark — half pride,
                  half mischief — is the whole reason this studio exists.
                </p>
                <p>
                  Too many kids' apps are built to keep children tapping, not thinking.
                  I wanted the opposite: games that treat kids as the brilliant,
                  curious people they are. So every KITOE game is ad-free, colorful,
                  and packed with real knowledge — animals, space, history, riddles,
                  science and more.
                </p>
                <p>
                  The monkey in our logo? That's the spirit of the studio: playful,
                  clever, and always swinging toward the next question.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.45} className="mt-8">
              <Link
                to="/apps"
                data-testid="about-explore-games-button"
                className="inline-flex items-center gap-2 px-7 py-4 rounded-2xl bg-coral text-white font-display font-extrabold text-lg border-2 border-ink shadow-brutal hover:-translate-x-1 hover:-translate-y-1 hover:shadow-brutal-lg transition-all duration-200"
              >
                See the games <ArrowRight size={20} />
              </Link>
            </Reveal>
          </div>

          <Reveal delay={0.3} className="lg:col-span-5 flex justify-center">
            <div className="w-64 h-64 sm:w-80 sm:h-80 rounded-[3rem] bg-skykit/40 border-2 border-ink shadow-brutal-lg flex items-center justify-center -rotate-3">
              <div className="animate-floaty">
                <MonkeyMark size={190} className="hidden sm:block" />
                <MonkeyMark size={140} className="sm:hidden" />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <Marquee dark />

      <section className="px-4 sm:px-8 py-20 sm:py-28">
        <div className="max-w-5xl mx-auto">
          <Reveal>
            <span className="text-xs font-extrabold uppercase tracking-[0.2em] text-coral">
              The manifesto
            </span>
            <h2 className="mt-2 font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight">
              Three promises, in every game
            </h2>
          </Reveal>

          <div className="mt-14 space-y-6">
            {chapters.map((c, i) => (
              <Reveal key={c.num} delay={i * 0.1}>
                <div
                  data-testid={`about-chapter-${c.num}`}
                  className="grid sm:grid-cols-12 gap-6 items-start rounded-[2rem] border-2 border-ink bg-white shadow-brutal p-7 sm:p-10"
                >
                  <span
                    className="sm:col-span-2 w-16 h-16 rounded-2xl border-2 border-ink flex items-center justify-center font-display font-black text-2xl"
                    style={{ backgroundColor: c.color }}
                  >
                    {c.num}
                  </span>
                  <div className="sm:col-span-10">
                    <h3 className="font-display font-black text-2xl sm:text-3xl tracking-tight">
                      {c.title}
                    </h3>
                    <p className="mt-3 text-base sm:text-lg text-ink/70 leading-relaxed">{c.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
